import {
  buildDetailedAmbientComparisonRows,
  getCopilotAmbientProductivity,
  getCopilotCampaignSourceLabel,
  getPhenologyTemporalReference,
  isCompleteCopilotAiText,
} from './copilotModel'
import {getCropLabel, isObservationalCrop, normalizeCropId} from '../../../shared/utils/cropCatalog'

const COPY = {
  es: {
    title: 'Análisis con IA',
    purpose:
      'Exportación estructurada de SpecifiC para continuar el análisis en otro modelo de IA.',
    instructionsTitle: 'Guía para el modelo que recibe este archivo',
    instructions: [
      'Usá únicamente la evidencia incluida y diferenciá datos observados, cálculos e interpretaciones.',
      'No atribuyas causalidad a una asociación entre capas sin evidencia adicional.',
      'Al señalar una conclusión, citá la sección, campaña, ambiente o fecha que la respalda.',
      'Indicá contradicciones, datos faltantes y límites antes de formular recomendaciones.',
      'Conservá las unidades y tratá los valores numéricos de las tablas con punto decimal.',
    ],
    metadata: 'Contexto y trazabilidad',
    providerRequestStatus: 'Estado de la consulta actual al proveedor de IA',
    storedInterpretationStatus: 'Estado de la interpretaci\u00f3n guardada',
    interpretation: 'Interpretación de resultados',
    observed: 'Relaciones observadas',
    ai: 'Interpretación generada con IA',
    qa: 'Consulta y respuesta',
    question: 'Pregunta',
    answer: 'Respuesta',
    agronomy: 'Seguimiento agronómico y climático',
    cropProfile: 'Datos del cultivo',
    methodology: 'Trazabilidad metodológica',
    sowingCalendar: 'Calendario de siembra',
    thermalTime: 'Parámetros de tiempo térmico',
    thermalLabels: {
      parameter: 'Parámetro',
      value: 'Valor',
      base: 'Temperatura base (°C)',
      optimum: 'Temperatura óptima (°C)',
      maximum: 'Temperatura máxima (°C)',
      source: 'Origen de los parámetros',
      cycleProfile: 'Perfil de ciclo',
      photoperiod: 'Respuesta al fotoperíodo',
      vernalization: 'Requerimiento de frío',
      enabled: 'Habilitado',
      disabled: 'Deshabilitado',
      sensitivePhase: 'Fase sensible',
      responseType: 'Tipo de respuesta',
      criticalHours: 'Fotoperíodo crítico (h)',
      sensitivity: 'Sensibilidad por hora (%)',
      minimumFactor: 'Factor mínimo de desarrollo (%)',
      appliedDays: 'Días con ajuste aplicado',
      meanFactor: 'Factor medio observado',
      vernalizationTemperatures: 'Temperaturas efectivas mínima / óptima / máxima (°C)',
      requiredUnits: 'Unidades de vernalización requeridas',
      accumulatedUnits: 'Unidades de vernalización acumuladas',
      completion: 'Requerimiento de frío cumplido (%)',
      stageThreshold: 'GDD acumulados para {{stage}}',
    },
    phenology: 'Estadío y cronología fenológica',
    phenologyClosed:
      'El período de análisis está cerrado. Esta estimación describe el corte indicado, no el estado actual del cultivo ni una cosecha confirmada.',
    phenologyDateUnknown:
      'Fecha de referencia no disponible: esta estimación no acredita el estado actual del cultivo.',
    cycleMetrics: 'Indicadores del ciclo',
    climateDaily: 'Serie climática diaria',
    vegetation: 'Índices de vegetación',
    vegetationSummary: 'Resumen de las imágenes analizadas',
    vegetationObservations: 'Observaciones por fecha',
    integrated: 'Cruce integrado de capas',
    dimensions: 'Dimensiones disponibles',
    mapLayers: 'Capas disponibles en el mapa integrado',
    ambientStructure: 'Estructura por ambiente',
    ambientVegetation: 'Evolución de los índices de vegetación promedio del ambiente',
    ambientPerformance: 'Rendimiento y margen del ambiente entre campañas',
    detailedComparison: 'Comparación por ambiente detallada',
    diagnostics: 'Diagnósticos y cruces calculados',
    findings: 'Señales verificadas',
    layers: 'Capas del lote',
    sources: 'Fuentes consultadas',
    limitations: 'Alcance y limitaciones del archivo',
    limitationsItems: [
      'El archivo incluye métricas, series y cruces tabulares; no incluye imágenes binarias ni geometrías crudas.',
      'El mapa interactivo se representa mediante disponibilidad de capas y resultados derivados, no como una imagen.',
      'Las asociaciones entre suelo, relieve, ambientación, vegetación, rendimiento y margen no demuestran causalidad por sí solas.',
      'Los estados no disponibles se conservan para evitar que otro modelo interprete la ausencia como un valor cero.',
    ],
    unavailable: 'No disponible',
    none: 'Sin datos',
    yes: 'Sí',
    no: 'No',
  },
  en: {
    title: 'AI analysis',
    purpose: 'Structured SpecifiC export for continuing the analysis in another AI model.',
    instructionsTitle: 'Guidance for the model receiving this file',
    instructions: [
      'Use only the included evidence and distinguish observed data, calculations, and interpretations.',
      'Do not infer causality from cross-layer associations without additional evidence.',
      'When stating a conclusion, cite the supporting section, campaign, management zone, or date.',
      'State contradictions, missing data, and limitations before making recommendations.',
      'Preserve units and treat numeric table values as dot-decimal numbers.',
    ],
    metadata: 'Context and traceability',
    providerRequestStatus: 'Current AI provider request status',
    storedInterpretationStatus: 'Saved AI interpretation status',
    interpretation: 'Results interpretation',
    observed: 'Observed relationships',
    ai: 'AI-generated interpretation',
    qa: 'Question and answer',
    question: 'Question',
    answer: 'Answer',
    agronomy: 'Agronomic and weather monitoring',
    cropProfile: 'Crop data',
    methodology: 'Methodological traceability',
    sowingCalendar: 'Sowing calendar',
    thermalTime: 'Thermal-time parameters',
    thermalLabels: {
      parameter: 'Parameter',
      value: 'Value',
      base: 'Base temperature (°C)',
      optimum: 'Optimum temperature (°C)',
      maximum: 'Maximum temperature (°C)',
      source: 'Parameter source',
      cycleProfile: 'Cycle profile',
      photoperiod: 'Photoperiod response',
      vernalization: 'Cold requirement',
      enabled: 'Enabled',
      disabled: 'Disabled',
      sensitivePhase: 'Sensitive phase',
      responseType: 'Response type',
      criticalHours: 'Critical photoperiod (h)',
      sensitivity: 'Sensitivity per hour (%)',
      minimumFactor: 'Minimum development factor (%)',
      appliedDays: 'Days with adjustment applied',
      meanFactor: 'Observed mean factor',
      vernalizationTemperatures: 'Minimum / optimum / maximum effective temperatures (°C)',
      requiredUnits: 'Required vernalization units',
      accumulatedUnits: 'Accumulated vernalization units',
      completion: 'Cold requirement completed (%)',
      stageThreshold: 'Accumulated GDD for {{stage}}',
    },
    phenology: 'Phenological stage and timeline',
    phenologyClosed:
      "The analysis period is closed. This estimate describes the stated cutoff, not the crop's current state or a confirmed harvest.",
    phenologyDateUnknown:
      "Reference date unavailable: this estimate does not establish the crop's current state.",
    cycleMetrics: 'Cycle indicators',
    climateDaily: 'Daily weather series',
    vegetation: 'Vegetation indices',
    vegetationSummary: 'Analyzed imagery summary',
    vegetationObservations: 'Observations by date',
    integrated: 'Integrated layer analysis',
    dimensions: 'Available dimensions',
    mapLayers: 'Layers available in the integrated map',
    ambientStructure: 'Management-zone structure',
    ambientVegetation: 'Evolution of average management-zone vegetation indices',
    ambientPerformance: 'Management-zone yield and margin across campaigns',
    detailedComparison: 'Detailed management-zone comparison',
    diagnostics: 'Calculated diagnostics and intersections',
    findings: 'Verified signals',
    layers: 'Plot layers',
    sources: 'Consulted sources',
    limitations: 'File scope and limitations',
    limitationsItems: [
      'The file includes metrics, time series, and tabular intersections; it excludes binary imagery and raw geometries.',
      'The interactive map is represented through layer availability and derived results, not as an image.',
      'Associations among soil, terrain, management zones, vegetation, yield, and margin do not prove causality by themselves.',
      'Unavailable states are retained so another model does not interpret absence as zero.',
    ],
    unavailable: 'Unavailable',
    none: 'No data',
    yes: 'Yes',
    no: 'No',
  },
}

const CROP_NAMES = {
  es: {maize: 'Maíz', corn: 'Maíz', soybean: 'Soja', soy: 'Soja', wheat: 'Trigo', peanut: 'Maní'},
  en: {
    maize: 'Corn',
    corn: 'Corn',
    soybean: 'Soybean',
    soy: 'Soybean',
    wheat: 'Wheat',
    peanut: 'Peanut',
  },
}

const normalizeLanguage = (locale) =>
  String(locale || '')
    .toLowerCase()
    .startsWith('en')
    ? 'en'
    : 'es'

const finiteNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null
  }

  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const normalizeText = (value) =>
  String(value ?? '')
    .replace(/\r\n?/g, '\n')
    .trim()

const inlineText = (value) => normalizeText(value).replace(/\s+/g, ' ')

const markdownCell = (value, fallback = '-') => {
  const text = inlineText(value)
  return (text || fallback).replace(/\\/g, '\\\\').replace(/\|/g, '\\|')
}

const numericValue = (value, digits = 6) => {
  const number = finiteNumberOrNull(value)
  if (number === null) {
    return '-'
  }

  return Number(number.toFixed(digits)).toString()
}

const percentageValue = (value) => {
  const number = finiteNumberOrNull(value)
  return number === null ? '-' : numericValue(number * 100)
}

const scalarValue = (value, copy) => {
  if (value === true) {
    return copy.yes
  }
  if (value === false) {
    return copy.no
  }
  if (typeof value === 'number') {
    return numericValue(value)
  }
  if (Array.isArray(value)) {
    return value.map((item) => scalarValue(item, copy)).join(', ') || '-'
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value)
  }

  return inlineText(value) || '-'
}

const sourceValue = (value, copy) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return scalarValue(value, copy)
  }

  const preferredKeys = ['name', 'label', 'provider', 'source', 'dataset', 'type']
  const preferredValue = preferredKeys
    .map((key) => value[key])
    .find((item) => typeof item === 'string' && item.trim())
  return preferredValue ? inlineText(preferredValue) : scalarValue(value, copy)
}

const markdownTable = (headers, rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return ''
  }

  const safeHeaders = headers.map((header) => markdownCell(header))
  const divider = safeHeaders.map(() => '---')
  const body = rows.map((row) => row.map((value) => markdownCell(value)).join(' | '))

  return [safeHeaders.join(' | '), divider.join(' | '), ...body]
    .map((line) => `| ${line} |`)
    .join('\n')
}

const yamlString = (value) =>
  `"${String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\r?\n/g, '\\n')}"`

const translated = (t, key, fallback, values = {}) => {
  if (typeof t !== 'function') {
    return fallback
  }

  const result = t(key, values)
  return result && result !== key ? result : fallback
}

const statusLabel = (status, t) =>
  translated(t, `source_status.${status || 'unavailable'}`, status || '-', {})

const cropLabel = (crop, language, t) => {
  const id = isObservationalCrop(crop) ? normalizeCropId(crop).toLowerCase() : String(crop || '').toLowerCase()
  const fallback = CROP_NAMES[language][String(crop || '').toLowerCase()] || getCropLabel(crop, language)
  return translated(t, `agronomy.crops.${id}`, fallback)
}

const stageLabel = (stageId, t) =>
  translated(t, `agronomy.stages.${stageId || 'unknown'}`, stageId || 'unknown')

const sanitizeFileNameCharacters = (value) => {
  const invalidCharacters = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*'])

  return Array.from(String(value || ''))
    .map((character) =>
      invalidCharacters.has(character) || character.charCodeAt(0) < 32 ? '-' : character
    )
    .join('')
}

const generatedDate = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime())
    ? new Date().toISOString().slice(0, 10)
    : date.toISOString().slice(0, 10)
}

export const createSpecificCopilotMarkdownFilename = ({
  fieldName,
  landName,
  locale = 'es',
  generatedAt,
} = {}) => {
  const language = normalizeLanguage(locale)
  const base = [COPY[language].title, fieldName, landName, generatedDate(generatedAt)]
    .map(inlineText)
    .filter(Boolean)
    .map((part) =>
      sanitizeFileNameCharacters(part)
        .replace(/\s+/g, ' ')
        .replace(/[. ]+$/g, '')
        .trim()
    )
    .filter(Boolean)
    .join(' - ')

  return `${base || COPY[language].title}.md`
}

const uniqueCampaigns = (integrated) => {
  const campaigns = []
  const seen = new Set()
  const candidates = [
    ...(Array.isArray(integrated?.campaigns) ? integrated.campaigns : []),
    ...(Array.isArray(integrated?.ambientRows)
      ? integrated.ambientRows.flatMap((row) =>
          Array.isArray(row?.campaigns) ? row.campaigns : []
        )
      : []),
  ]

  candidates.forEach((campaign) => {
    const key = inlineText(campaign?.campaignKey || campaign?.campaign)
    if (!key || seen.has(key)) {
      return
    }
    seen.add(key)
    campaigns.push(campaign)
  })

  return campaigns
}

const ambientDescriptors = (integrated, locale) => {
  const rows = Array.isArray(integrated?.ambientRows) ? integrated.ambientRows : []
  const values = rows.map((row) => row?.ambientClass)

  return rows
    .map((row) => ({
      row,
      ...getCopilotAmbientProductivity(row?.ambientClass, values, locale),
    }))
    .sort((left, right) => {
      if (left.rank !== null && right.rank !== null) {
        return right.rank - left.rank
      }
      return String(right.code).localeCompare(String(left.code), locale)
    })
}

const mergeIndexObservations = (ndvi, evi) => {
  const byDate = new Map()
  const add = (indexName, observations) => {
    ;(Array.isArray(observations) ? observations : []).forEach((observation) => {
      const date = inlineText(observation?.date)
      if (!date) {
        return
      }
      const current = byDate.get(date) || {date}
      current[indexName] = observation
      byDate.set(date, current)
    })
  }

  add('ndvi', ndvi)
  add('evi', evi)
  return Array.from(byDate.values()).sort((left, right) => left.date.localeCompare(right.date))
}

const keyValueTable = (object, copy, excludedKeys = []) => {
  if (!object || typeof object !== 'object') {
    return ''
  }

  const excluded = new Set(excludedKeys)
  return markdownTable(
    ['Campo', 'Valor'],
    Object.entries(object)
      .filter(([key, value]) => !excluded.has(key) && value !== undefined)
      .map(([key, value]) => [key, scalarValue(value, copy)])
  )
}

const appendSection = (parts, level, title, content) => {
  const value = normalizeText(content)
  if (!value) {
    return
  }

  parts.push(`${'#'.repeat(level)} ${title}\n\n${value}`)
}

const buildAgronomySections = ({analysis, copy, language, t}) => {
  const parts = []
  const agronomy = analysis?.agronomicAnalysis || {}
  if (agronomy.status !== 'available') {
    return parts
  }

  const profile = agronomy.profile || analysis.agronomicProfile || {}
  const observational = isObservationalCrop(profile.crop)
  const phenology = agronomy.phenology || {}
  const thermalTime = analysis?.agronomicProfile?.thermalTime || profile.thermalTime || {}
  const sowingCalendarProvenance = agronomy.sowingCalendar?.provenance || {}
  const thermalModelProvenance = phenology.provenance || thermalTime.provenance || {}
  const developmentModifiers = phenology.developmentModifiers || {}
  const climate = agronomy.climate || {}
  const summary = climate.summary || {}
  const periodPolicy = agronomy.periodPolicy || {}
  const genotype =
    profile.genotype ||
    (analysis.agronomicCatalog || []).find((item) => item.id === profile.genotypeId)?.label ||
    profile.genotypeId

  appendSection(
    parts,
    3,
    copy.cropProfile,
    markdownTable(
      ['Dato', 'Valor'],
      [
        ['Cultivo', cropLabel(profile.crop, language, t)],
        ...(!observational ? [['Híbrido o variedad', genotype || '-']] : []),
        [observational ? translated(t, 'agronomy.observational.start_short', language === 'en' ? 'Period start' : 'Inicio del período') : 'Fecha de siembra', profile.sowingDate || '-'],
        [observational ? translated(t, 'agronomy.observational.end_short', language === 'en' ? 'Period end' : 'Fin del período') : 'Fecha de cosecha', profile.harvestDate || '-'],
        ['Fecha final efectiva del análisis', periodPolicy.analysisEndDate || '-'],
        ['Período cerrado', scalarValue(periodPolicy.closed, copy)],
        ['Límite automático aplicado', scalarValue(periodPolicy.cappedAutomatically, copy)],
      ]
    )
  )

  if (observational) {
    parts.push(translated(t, 'agronomy.observational.notice', language === 'en'
      ? 'Weather, indices and available layers are observed. No calibrated phenology model is available for this crop; stages and growing degree days are not estimated.'
      : 'Se observan clima, índices y capas disponibles. Este cultivo no cuenta con un modelo fenológico calibrado; no se estiman estadíos ni grados-día.'))
  }

  if (!observational) {
  const methodologyRows = []
  if (sowingCalendarProvenance.modelVersion) {
    methodologyRows.push([
      copy.sowingCalendar,
      sowingCalendarProvenance.sourceType,
      sowingCalendarProvenance.purpose,
      sowingCalendarProvenance.scope,
    ])
  }
  if (thermalModelProvenance.modelVersion) {
    methodologyRows.push([
      copy.thermalTime,
      thermalModelProvenance.parameterSource,
      thermalModelProvenance.purpose,
      thermalModelProvenance.scope,
    ])
  }
  appendSection(
    parts,
    3,
    copy.methodology,
    markdownTable(
      ['Componente', 'Origen', 'Uso', 'Alcance'],
      methodologyRows
    )
  )

  const labels = copy.thermalLabels
  const photoperiod = thermalTime.photoperiod || developmentModifiers.photoperiod
  const vernalization = thermalTime.vernalization || developmentModifiers.vernalization
  const sensitivePhase = (modifier) =>
    [stageLabel(modifier?.appliesFromStageId, t), stageLabel(modifier?.appliesUntilStageId, t)]
      .filter(Boolean)
      .join(' - ')
  const thermalRows = [
    [labels.base, numericValue(thermalTime.baseTemperatureC ?? phenology.baseTemperatureC)],
    [
      labels.optimum,
      numericValue(thermalTime.optimumTemperatureC ?? phenology.optimumTemperatureC),
    ],
    [
      labels.maximum,
      numericValue(thermalTime.maximumTemperatureC ?? phenology.maximumTemperatureC),
    ],
    [labels.source, thermalTime.source || phenology.thermalTimeSource || '-'],
    [labels.cycleProfile, thermalModelProvenance.profileId || profile.genotypeId || '-'],
    ...(Array.isArray(thermalTime.stages)
      ? thermalTime.stages.map((stage) => [
          labels.stageThreshold.replace('{{stage}}', stageLabel(stage?.stageId, t)),
          numericValue(stage?.thresholdGdd),
        ])
      : []),
    ...(photoperiod
      ? [
          [`${labels.photoperiod} · ${labels.enabled}`, scalarValue(photoperiod.enabled, copy)],
          [`${labels.photoperiod} · ${labels.responseType}`, photoperiod.responseType || '-'],
          [`${labels.photoperiod} · ${labels.sensitivePhase}`, sensitivePhase(photoperiod)],
          [
            `${labels.photoperiod} · ${labels.criticalHours}`,
            numericValue(photoperiod.criticalHours),
          ],
          [
            `${labels.photoperiod} · ${labels.sensitivity}`,
            percentageValue(photoperiod.sensitivityPerHour),
          ],
          [
            `${labels.photoperiod} · ${labels.minimumFactor}`,
            percentageValue(photoperiod.minimumFactor),
          ],
          [
            `${labels.photoperiod} · ${labels.appliedDays}`,
            numericValue(developmentModifiers.photoperiod?.appliedDays),
          ],
          [
            `${labels.photoperiod} · ${labels.meanFactor}`,
            numericValue(developmentModifiers.photoperiod?.meanFactor),
          ],
        ]
      : []),
    ...(vernalization
      ? [
          [`${labels.vernalization} · ${labels.enabled}`, scalarValue(vernalization.enabled, copy)],
          [`${labels.vernalization} · ${labels.sensitivePhase}`, sensitivePhase(vernalization)],
          [
            `${labels.vernalization} · ${labels.vernalizationTemperatures}`,
            [
              numericValue(vernalization.minimumTemperatureC),
              numericValue(vernalization.optimumTemperatureC),
              numericValue(vernalization.maximumTemperatureC),
            ].join(' / '),
          ],
          [
            `${labels.vernalization} · ${labels.requiredUnits}`,
            numericValue(vernalization.requiredUnits),
          ],
          [
            `${labels.vernalization} · ${labels.minimumFactor}`,
            percentageValue(vernalization.minimumFactor),
          ],
          [
            `${labels.vernalization} · ${labels.accumulatedUnits}`,
            numericValue(developmentModifiers.vernalization?.accumulatedUnits),
          ],
          [
            `${labels.vernalization} · ${labels.completion}`,
            numericValue(developmentModifiers.vernalization?.completionPercent),
          ],
        ]
      : []),
  ]
  appendSection(
    parts,
    3,
    copy.thermalTime,
    markdownTable([labels.parameter, labels.value], thermalRows)
  )

  const timeline = Array.isArray(phenology.timeline) ? phenology.timeline : []
  const temporalReference = getPhenologyTemporalReference(agronomy)
  const phenologySummary = markdownTable(
    ['Dato', 'Valor'],
    [
      ['Estadío estimado', stageLabel(phenology.stageId, t)],
      [
        language === 'en' ? 'Stage reference date' : 'Fecha de referencia del estadío',
        temporalReference.referenceDate || '-',
      ],
      ['Confianza', phenology.confidence || '-'],
      ['Progreso del ciclo (%)', numericValue(phenology.progressPercent)],
      ['GDD acumulados', numericValue(phenology.accumulatedGdd)],
      ['GDD hasta el próximo estadío', numericValue(phenology.gddToNextStage)],
    ]
  )
  const timelineTable = markdownTable(
    ['Estadío', 'Fecha', 'Estado', 'Base', 'Umbral GDD'],
    timeline.map((item) => [
      stageLabel(item?.stageId, t),
      item?.date || '-',
      item?.status || '-',
      item?.basis || '-',
      numericValue(item?.thresholdGdd),
    ])
  )
  appendSection(
    parts,
    3,
    copy.phenology,
    [
      phenologySummary,
      temporalReference.closed
        ? translated(t, 'agronomy.phenology_closed_notice', copy.phenologyClosed)
        : '',
      !temporalReference.referenceDate
        ? translated(t, 'agronomy.phenology_date_unknown', copy.phenologyDateUnknown)
        : '',
      timelineTable,
    ]
      .filter(Boolean)
      .join('\n\n')
  )
  }

  appendSection(
    parts,
    3,
    observational ? (language === 'en' ? 'Observed-period indicators' : 'Indicadores del período observado') : copy.cycleMetrics,
    markdownTable(
      ['Indicador', 'Valor', 'Unidad'],
      [
        ...(!observational ? [
          ['GDD acumulados', phenology.accumulatedGdd, 'GDD'],
          ['GDD hasta el próximo estadío', phenology.gddToNextStage, 'GDD'],
        ] : []),
        ['Precipitaciones acumuladas', summary.precipitationMm, 'mm'],
        ['Evapotranspiración de referencia acumulada', summary.referenceEt0Mm, 'mm'],
        ['Balance climático indicativo', summary.climaticBalanceMm, 'mm'],
        ['Temperatura media', summary.meanTemperatureC, '°C'],
        ['Temperatura mínima', summary.minimumTemperatureC, '°C'],
        ['Temperatura máxima', summary.maximumTemperatureC, '°C'],
        ['Días con máxima ≥ 35 °C', summary.hotDays, 'días'],
        ['Días con mínima ≤ 0 °C', summary.frostDays, 'días'],
        ['Mayor período sin lluvia', summary.longestDrySpellDays, 'días'],
        ['Completitud climática', summary.completenessPercent, '%'],
        ['Fuente climática', climate.source?.name || '-', '-'],
      ].map(([label, value, unit]) => [
        label,
        typeof value === 'number' ? numericValue(value) : value,
        unit,
      ])
    )
  )

  const daily = Array.isArray(climate.daily) ? climate.daily : []
  appendSection(
    parts,
    3,
    copy.climateDaily,
    markdownTable(
      [
        'Fecha',
        'T mín (°C)',
        'T media (°C)',
        'T máx (°C)',
        'Precipitación (mm)',
        'ET0 (mm)',
        ...(!observational ? ['GDD diario', 'GDD acumulado'] : []),
      ],
      daily.map((item) => [
        item?.date,
        numericValue(item?.minTemperatureC),
        numericValue(item?.meanTemperatureC),
        numericValue(item?.maxTemperatureC),
        numericValue(item?.precipitationMm),
        numericValue(item?.referenceEt0Mm),
        ...(!observational ? [numericValue(item?.dailyGdd), numericValue(item?.cumulativeGdd)] : []),
      ])
    )
  )

  return parts
}

const indexSummaryRows = (name, payload, copy) => {
  const index = payload || {}
  return [
    [name, 'Estado', index.status || copy.unavailable],
    [name, 'Fuente', index.source || '-'],
    [name, 'Período', [index.dateFrom, index.dateTo].filter(Boolean).join(' al ') || '-'],
    [name, 'Criterio del período', index.periodBasis || '-'],
    ...(index.catalogCoverage?.status ? [[name, 'Cobertura del catálogo de fechas', index.catalogCoverage.status], [name, 'Motivo del catálogo parcial', index.catalogCoverage.reason || '-']] : []),
    ...(index.samplingPolicy ? [[name, 'Máximo de imágenes analizadas', index.samplingPolicy.maximumImagesTotal], [name, 'Máximo de imágenes por mes', index.samplingPolicy.maximumImagesPerMonth], [name, 'Criterio de muestreo temporal', index.samplingPolicy.basis || '-']] : []),
    [name, 'Imágenes disponibles', numericValue(index.availableImageCount)],
    [name, 'Imágenes seleccionadas', numericValue(index.selectedImageCount)],
    [name, 'Imágenes analizadas', numericValue(index.analyzedImageCount)],
    [name, 'Cobertura válida mínima (%)', numericValue(index.minimumValidCoveragePercent)],
    [name, 'Promedio más reciente', numericValue(index.latestMean)],
    [name, 'Promedio de referencia', numericValue(index.baselineMean)],
    [name, 'Cambio frente al promedio de referencia', numericValue(index.changeFromBaseline)],
    [
      name,
      'Cambio frente al promedio de referencia (%)',
      numericValue(index.changePercentFromBaseline),
    ],
    [name, 'Mayor caída consecutiva', numericValue(index.largestConsecutiveDrop)],
  ]
}

const indexObservationRows = (name, payload) =>
  (Array.isArray(payload?.observations) ? payload.observations : []).map((item) => [
    name,
    item?.date,
    numericValue(item?.mean),
    numericValue(item?.median),
    numericValue(item?.standardDeviation),
    numericValue(item?.p10),
    numericValue(item?.p90),
    numericValue(item?.lowVegetationPercent),
    numericValue(item?.cloudPercent),
  ])

const buildVegetationSections = ({analysis, copy}) => {
  const parts = []
  const ndvi = analysis?.recentNdvi || {}
  const evi = analysis?.recentEvi || {}
  const hasRequestedIndex = [ndvi, evi].some((item) => item.status !== 'not_requested')
  if (!hasRequestedIndex) {
    return parts
  }

  appendSection(
    parts,
    3,
    copy.vegetationSummary,
    markdownTable(
      ['Índice', 'Dato', 'Valor'],
      [...indexSummaryRows('NDVI', ndvi, copy), ...indexSummaryRows('EVI', evi, copy)]
    )
  )
  appendSection(
    parts,
    3,
    copy.vegetationObservations,
    markdownTable(
      [
        'Índice',
        'Fecha',
        'Promedio',
        'Mediana',
        'Desvío estándar',
        'P10',
        'P90',
        'Vegetación baja (%)',
        'Nubosidad (%)',
      ],
      [...indexObservationRows('NDVI', ndvi), ...indexObservationRows('EVI', evi)]
    )
  )

  return parts
}

const buildIntegratedSections = ({analysis, copy, locale, t}) => {
  const parts = []
  const cross = analysis?.crossLayerAnalysis || {}
  const integrated = cross.integratedAnalysis || {}
  if (integrated.status !== 'available') {
    return parts
  }

  const descriptors = ambientDescriptors(integrated, locale)
  const campaigns = uniqueCampaigns(integrated)
  const availableDimensions = integrated.availableDimensions || {}
  const availableMapLayers = integrated.map?.availableLayers || {}

  appendSection(
    parts,
    3,
    copy.dimensions,
    markdownTable(
      ['Dimensión', 'Disponible'],
      Object.entries(availableDimensions).map(([name, value]) => [name, scalarValue(value, copy)])
    )
  )
  appendSection(
    parts,
    3,
    copy.mapLayers,
    markdownTable(
      ['Capa', 'Disponible'],
      Object.entries(availableMapLayers).map(([name, value]) => [name, scalarValue(value, copy)])
    )
  )

  appendSection(
    parts,
    3,
    copy.ambientStructure,
    markdownTable(
      [
        'Ambiente',
        'Descripción',
        'Superficie (ha)',
        'Suelo dominante',
        'IP del suelo dominante',
        'Participación del suelo dominante (%)',
        'Altura mínima (m)',
        'Altura promedio (m)',
        'Altura máxima (m)',
        'Desnivel (m)',
      ],
      descriptors.map((descriptor) => [
        descriptor.code,
        descriptor.name,
        numericValue(descriptor.row?.surfaceHa),
        descriptor.row?.dominantSoilUnit,
        numericValue(descriptor.row?.dominantSoilProductivityIndex),
        numericValue(descriptor.row?.dominantSoilSharePercent),
        numericValue(descriptor.row?.terrain?.minElevationM),
        numericValue(descriptor.row?.terrain?.meanElevationM),
        numericValue(descriptor.row?.terrain?.maxElevationM),
        numericValue(descriptor.row?.terrain?.elevationRangeM),
      ])
    )
  )

  const vegetationRows = descriptors.flatMap((descriptor) =>
    mergeIndexObservations(
      descriptor.row?.ndvi?.observations,
      descriptor.row?.evi?.observations
    ).map((item) => [
      descriptor.code,
      descriptor.name,
      item.date,
      numericValue(item.ndvi?.mean),
      numericValue(item.evi?.mean),
    ])
  )
  appendSection(
    parts,
    3,
    copy.ambientVegetation,
    markdownTable(
      ['Ambiente', 'Descripción', 'Fecha', 'NDVI promedio', 'EVI promedio'],
      vegetationRows
    )
  )

  const performanceRows = descriptors.flatMap((descriptor) =>
    (Array.isArray(descriptor.row?.campaigns) ? descriptor.row.campaigns : []).map((campaign) => [
      descriptor.code,
      descriptor.name,
      campaign?.campaign || campaign?.campaignKey,
      cropLabel(campaign?.crop, normalizeLanguage(locale), t),
      getCopilotCampaignSourceLabel(campaign, locale) || '-',
      campaign?.sourceKey || campaign?.reportId || campaign?.layerId || '-',
      numericValue(descriptor.row?.surfaceHa),
      numericValue(campaign?.coveredAreaHa),
      numericValue(descriptor.row?.terrain?.meanElevationM),
      numericValue(campaign?.coveragePercent),
      numericValue(campaign?.yieldPointCount),
      numericValue(campaign?.layerYieldTonsHa),
      numericValue(campaign?.economicYieldTonsHa),
      numericValue(campaign?.grossMarginUsdHa),
      campaign?.calculationBasis || '-',
    ])
  )
  appendSection(
    parts,
    3,
    copy.ambientPerformance,
    markdownTable(
      [
        'Ambiente',
        'Descripción',
        'Campaña',
        'Cultivo',
        'Reporte',
        'Clave de fuente',
        'Superficie (ha)',
        'Superficie con rendimiento (ha)',
        'Altura promedio (m)',
        'Cobertura (%)',
        'Puntos de rendimiento',
        'Rendimiento del mapa (t/ha)',
        'Rendimiento económico (t/ha)',
        'Margen bruto (USD/ha)',
        'Base de cálculo',
      ],
      performanceRows
    )
  )

  const tripleCampaigns = Array.isArray(integrated.tripleCross?.campaigns)
    ? integrated.tripleCross.campaigns
    : []
  const detailedRows = tripleCampaigns.flatMap((campaign) => {
    const cells = buildDetailedAmbientComparisonRows(
      campaign?.cells,
      descriptors.map((descriptor) => descriptor.row?.ambientClass),
      locale
    )
    return cells.map((cell) => [
      campaign?.campaign || campaign?.campaignKey,
      cropLabel(campaign?.crop, normalizeLanguage(locale), t),
      getCopilotCampaignSourceLabel(campaign, locale) || '-',
      campaign?.sourceKey || campaign?.reportId || campaign?.layerId || '-',
      cell?.descriptor?.code || cell?.ambientClass,
      cell?.descriptor?.name || '-',
      cell?.soilUnit,
      numericValue(cell?.soilProductivityIndex),
      numericValue(cell?.surfaceHa),
      numericValue(cell?.coveredAreaHa),
      numericValue(cell?.meanElevationM),
      numericValue(cell?.yieldPointCount),
      numericValue(cell?.economicYieldTonsHa),
      numericValue(cell?.grossMarginUsdHa),
    ])
  })
  appendSection(
    parts,
    3,
    copy.detailedComparison,
    markdownTable(
      [
        'Campaña',
        'Cultivo',
        'Reporte',
        'Clave de fuente',
        'Ambiente',
        'Descripción',
        'Unidad de suelo',
        'IP',
        'Superficie (ha)',
        'Superficie con rendimiento (ha)',
        'Altura promedio (m)',
        'Puntos de rendimiento',
        'Rendimiento económico (t/ha)',
        'Margen bruto (USD/ha)',
      ],
      detailedRows
    )
  )

  const campaignTable = markdownTable(
    [
      'Campaña',
      'Cultivo',
      'Reporte',
      'Clave de fuente',
      'Estado',
      'Superficie cubierta (ha)',
      'Puntos de rendimiento',
    ],
    campaigns.map((campaign) => [
      campaign?.campaign || campaign?.campaignKey,
      cropLabel(campaign?.crop, normalizeLanguage(locale), t),
      getCopilotCampaignSourceLabel(campaign, locale) || '-',
      campaign?.sourceKey || campaign?.reportId || campaign?.layerId || '-',
      campaign?.status || '-',
      numericValue(campaign?.coveredAreaHa),
      numericValue(campaign?.yieldPointCount),
    ])
  )
  const terrainTable = keyValueTable(
    {
      terrainResolutionXYMeters: integrated.terrainResolutionXYMeters,
      terrainResolutionZMeters: integrated.terrainResolutionZMeters,
      ...(integrated.terrainSummary || {}),
    },
    copy
  )
  const pairTable = markdownTable(
    ['Cruce', 'Estado'],
    (Array.isArray(cross.analyzedPairs) ? cross.analyzedPairs : []).map((pair) => [
      pair?.id,
      pair?.status,
    ])
  )
  const diagnosticPayload = Object.fromEntries(
    Object.entries(cross).filter(
      ([key, value]) =>
        !['status', 'analyzedPairs', 'integratedAnalysis'].includes(key) && value !== undefined
    )
  )
  const diagnosticTable = keyValueTable(diagnosticPayload, copy)
  appendSection(
    parts,
    3,
    copy.diagnostics,
    [campaignTable, terrainTable, pairTable, diagnosticTable].filter(Boolean).join('\n\n')
  )

  return parts
}

const buildInterpretationSections = ({analysis, interpretation, copy}) => {
  const parts = []
  const observed = (Array.isArray(interpretation) ? interpretation : [])
    .map((item) => inlineText(item?.text))
    .filter(Boolean)
  if (observed.length > 0) {
    appendSection(parts, 3, copy.observed, observed.map((item) => `- ${item}`).join('\n'))
  }

  const aiText = normalizeText(analysis?.aiInterpretation?.text)
  if (
    analysis?.aiInterpretation?.status === 'available' &&
    aiText &&
    isCompleteCopilotAiText(aiText)
  ) {
    appendSection(parts, 3, copy.ai, aiText)
  }

  return parts
}

const buildFindingSections = ({analysis, copy}) => {
  const findings = Array.isArray(analysis?.findings) ? analysis.findings : []
  if (findings.length === 0) {
    return []
  }

  const content = findings
    .map((finding, index) => {
      const evidence = markdownTable(
        ['Dato', 'Valor'],
        (Array.isArray(finding?.evidence) ? finding.evidence : []).map((item) => [
          item?.label,
          item?.value,
        ])
      )
      return [
        `### ${index + 1}. ${inlineText(finding?.title) || finding?.id || 'Señal'}`,
        `- Estado: ${finding?.severity || '-'}`,
        inlineText(finding?.summary),
        evidence,
      ]
        .filter(Boolean)
        .join('\n\n')
    })
    .join('\n\n')

  return [`## ${copy.findings}\n\n${content}`]
}

const buildLayerAndSourceSections = ({analysis, copy, t}) => {
  const parts = []
  appendSection(
    parts,
    2,
    copy.layers,
    markdownTable(
      ['Capa', 'Estado', 'Tipo', 'Fuente', 'Cantidad', 'Observaciones', 'Clases', 'Años'],
      (Array.isArray(analysis?.layerCatalog) ? analysis.layerCatalog : []).map((layer) => [
        translated(t, `layers.${layer?.id}`, layer?.id || '-'),
        statusLabel(layer?.status, t),
        layer?.kind,
        sourceValue(layer?.source, copy),
        numericValue(layer?.count),
        numericValue(layer?.observationCount),
        numericValue(layer?.classCount),
        Array.isArray(layer?.years) ? layer.years.join(', ') : layer?.years,
      ])
    )
  )
  appendSection(
    parts,
    2,
    copy.sources,
    markdownTable(
      ['Fuente', 'Tipo', 'Estado', 'Detalle'],
      (Array.isArray(analysis?.sources) ? analysis.sources : []).map((source) => [
        source?.label,
        source?.type,
        statusLabel(source?.status, t),
        source?.detail,
      ])
    )
  )

  return parts
}

export const buildSpecificCopilotMarkdown = ({
  analysis,
  interpretation = [],
  question = '',
  answer = '',
  t,
  locale = 'es',
  generatedAt = new Date(),
} = {}) => {
  const language = normalizeLanguage(locale)
  const copy = COPY[language]
  const scope = analysis?.scope || {}
  const fieldName = inlineText(scope.fieldName)
  const landName = inlineText(scope.landName)
  const title = [copy.title, fieldName, landName].filter(Boolean).join(' - ')
  const generatedIso =
    generatedAt instanceof Date ? generatedAt.toISOString() : new Date(generatedAt).toISOString()
  const agronomy = analysis?.agronomicAnalysis || {}
  const profile = agronomy.profile || analysis?.agronomicProfile || {}
  const periodEnd = agronomy.periodPolicy?.analysisEndDate || profile.harvestDate || ''
  const parts = [
    [
      '---',
      'document_type: "specific_ai_land_analysis"',
      'schema_version: "1.0"',
      `language: ${yamlString(language)}`,
      `generated_at: ${yamlString(generatedIso)}`,
      `analyzed_at: ${yamlString(analysis?.analysisMetadata?.analyzedAt || '')}`,
      `analysis_date: ${yamlString(analysis?.analysisMetadata?.analysisDate || '')}`,
      `field: ${yamlString(fieldName)}`,
      `plot: ${yamlString(landName)}`,
      `crop: ${yamlString(profile.crop || '')}`,
      `analysis_period_start: ${yamlString(profile.sowingDate || '')}`,
      `analysis_period_end: ${yamlString(periodEnd)}`,
      `provider_status: ${yamlString(analysis?.providerStatus || '')}`,
      `ai_interpretation_status: ${yamlString(
        analysis?.aiInterpretation?.status || 'not_requested'
      )}`,
      `analysis_mode: ${yamlString(analysis?.mode || '')}`,
      'contains_raw_geometries: false',
      'contains_binary_images: false',
      '---',
    ].join('\n'),
    `# ${title || copy.title}`,
    `> ${copy.purpose}`,
    `## ${copy.instructionsTitle}\n\n${copy.instructions.map((item) => `- ${item}`).join('\n')}`,
  ]

  appendSection(
    parts,
    2,
    copy.metadata,
    markdownTable(
      ['Dato', 'Valor'],
      [
        ['Campo', fieldName || '-'],
        ['Lote', landName || '-'],
        ['Generado (ISO 8601)', generatedIso],
        ['Análisis ejecutado', analysis?.analysisMetadata?.analyzedAt || '-'],
        ['Fecha lógica del análisis', analysis?.analysisMetadata?.analysisDate || '-'],
        ['Análisis reutilizado', scalarValue(analysis?.analysisMetadata?.reused, copy)],
        ['Modo', analysis?.mode || '-'],
        [copy.providerRequestStatus, analysis?.providerStatus || '-'],
        [copy.storedInterpretationStatus, analysis?.aiInterpretation?.status || 'not_requested'],
      ]
    )
  )

  const interpretationParts = buildInterpretationSections({analysis, interpretation, copy})
  if (interpretationParts.length > 0) {
    parts.push(`## ${copy.interpretation}`)
    parts.push(...interpretationParts)
  }

  if (inlineText(question) || inlineText(answer)) {
    appendSection(
      parts,
      2,
      copy.qa,
      [
        `### ${copy.question}\n\n${normalizeText(question) || '-'}`,
        `### ${copy.answer}\n\n${normalizeText(answer) || '-'}`,
      ].join('\n\n')
    )
  }

  const agronomyParts = buildAgronomySections({analysis, copy, language, t})
  if (agronomyParts.length > 0) {
    parts.push(`## ${copy.agronomy}`)
    parts.push(...agronomyParts)
  }

  const vegetationParts = buildVegetationSections({analysis, copy})
  if (vegetationParts.length > 0) {
    parts.push(`## ${copy.vegetation}`)
    parts.push(...vegetationParts)
  }

  const integratedParts = buildIntegratedSections({analysis, copy, locale, t})
  if (integratedParts.length > 0) {
    parts.push(`## ${copy.integrated}`)
    parts.push(...integratedParts)
  }

  parts.push(...buildFindingSections({analysis, copy}))
  parts.push(...buildLayerAndSourceSections({analysis, copy, t}))

  if (inlineText(analysis?.notice)) {
    appendSection(parts, 2, 'Aviso del análisis', analysis.notice)
  }
  appendSection(
    parts,
    2,
    copy.limitations,
    copy.limitationsItems.map((item) => `- ${item}`).join('\n')
  )

  return `${parts
    .filter(Boolean)
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')}\n`
}

export const downloadSpecificCopilotMarkdown = async ({
  analysis,
  interpretation,
  question,
  answer,
  t,
  locale = 'es',
} = {}) => {
  const generatedAt = new Date()
  const markdown = buildSpecificCopilotMarkdown({
    analysis,
    interpretation,
    question,
    answer,
    t,
    locale,
    generatedAt,
  })
  const filename = createSpecificCopilotMarkdownFilename({
    fieldName: analysis?.scope?.fieldName,
    landName: analysis?.scope?.landName,
    locale,
    generatedAt,
  })
  const url = URL.createObjectURL(new Blob([markdown], {type: 'text/markdown;charset=utf-8'}))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)

  return {filename, markdown}
}
